import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, SectionLabel, MaskedLine } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BUSINESS_TYPES = ["Retailer", "Distributor", "Contractor Supply", "Hardware Store", "Other"];

const inputCls =
  "w-full border border-zinc-800 bg-[#111111] px-4 py-3.5 text-sm text-zinc-100 placeholder-zinc-600 outline-none transition-colors duration-300 focus:border-zinc-400";

export default function Dealer() {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", business_type: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await axios.post(`${API}/dealer-inquiries`, {
        name: form.name,
        company: form.company,
        email: form.email,
        phone: form.phone || null,
        business_type: form.business_type || null,
        message: form.message || null,
      });
      toast.success("Application received. Our dealer team will reach out.");
      setForm({ name: "", company: "", email: "", phone: "", business_type: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please email hello@chwasupply.com");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="dealer" data-testid="dealer-section" className="border-t border-zinc-800">
      <div className="mx-auto grid max-w-[1600px] gap-16 px-6 py-28 md:grid-cols-2 md:px-12 md:py-40">
        <div>
          <Reveal>
            <SectionLabel index="06" title="Dealers" />
          </Reveal>
          <div className="mt-10">
            <MaskedLine delay={0.05}>
              <h2 className="font-display text-5xl font-bold uppercase leading-[0.9] tracking-tight text-zinc-50 sm:text-6xl lg:text-8xl">
                Sell CHWA.
              </h2>
            </MaskedLine>
          </div>
          <Reveal delay={0.15}>
            <p className="mt-8 max-w-md text-base leading-relaxed text-zinc-400 md:text-lg">
              Interested in selling CHWA products? Become an authorized dealer.
            </p>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-zinc-500">
              Authorized dealers receive first allocation of the CHWA-01 launch, protected territories, and direct support from our Texas headquarters.
            </p>
            <div className="mt-10 space-y-3 text-xs uppercase tracking-[0.25em] text-zinc-500">
              <p data-testid="dealer-point-allocation">— First launch allocation</p>
              <p data-testid="dealer-point-territory">— Protected territories</p>
              <p data-testid="dealer-point-support">— Direct Texas HQ support</p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <form data-testid="dealer-form" onSubmit={submit} className="space-y-4 border border-zinc-800 bg-[#0D0D0D] p-6 md:p-10">
            <div className="grid gap-4 sm:grid-cols-2">
              <input data-testid="dealer-input-name" required placeholder="Full Name *" value={form.name} onChange={set("name")} className={inputCls} />
              <input data-testid="dealer-input-company" required placeholder="Company *" value={form.company} onChange={set("company")} className={inputCls} />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <input data-testid="dealer-input-email" required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} />
              <input data-testid="dealer-input-phone" placeholder="Phone" value={form.phone} onChange={set("phone")} className={inputCls} />
            </div>
            <select
              data-testid="dealer-input-business-type"
              value={form.business_type}
              onChange={set("business_type")}
              className={`${inputCls} ${form.business_type ? "" : "text-zinc-600"}`}
            >
              <option value="" disabled>Business Type</option>
              {BUSINESS_TYPES.map((t) => (
                <option key={t} value={t} className="text-zinc-100">{t}</option>
              ))}
            </select>
            <textarea
              data-testid="dealer-input-message"
              placeholder="Tell us about your business"
              rows={4}
              value={form.message}
              onChange={set("message")}
              className={`${inputCls} resize-none`}
            />
            <button
              data-testid="dealer-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-zinc-100 px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-zinc-950 transition-colors duration-300 hover:bg-white disabled:opacity-50"
            >
              {loading ? "Submitting..." : "Apply to Become a Dealer"}
            </button>
            <p className="text-center text-[11px] leading-relaxed text-zinc-600">
              Prefer email? Reach us at hello@chwasupply.com
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
